import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["bordered", "children", "condensed", "flush", "height", "hover", "responsive", "ScrollContainer", "truncated", "responsiveWrapperTokenProps"];
import { forwardRef } from 'react';
import styled from 'foundations-theming/utils';
import { Section } from '../Section';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { ScrollContainer as ScrollContainerComponent } from '../ScrollContainer';
import { usePanelContext } from '../../context';
import { useTableStyles } from './Table.styles';
import { useTableContext } from './TableContext';
import { useCardContext } from '../Card/CardContext';
import { deepMerge } from '../../utils/deepMerge';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledTable = styled.table.withConfig({
  displayName: "Table__StyledTable",
  componentId: "kpqqsm-0"
})(({
  theme,
  $cssStyles,
  $styleOverrides,
  $flush,
  $bordered,
  $condensed,
  $hover,
  $responsive,
  $truncated,
  $inPanel,
  $inCard
}) => {
  const {
    tableHeaderHeight,
    tableCellHeight,
    tableCellHeightCompact,
    defaultPadding,
    condensedPadding,
    tableCellVerticalAlignment
  } = useTableStyles();
  return deepMerge(Object.assign({
    fontSize: theme.typography['body-100'].fontSize,
    backgroundColor: theme.color['fill-surface-default'],
    borderCollapse: 'collapse',
    width: '100%'
  }, ($flush || $responsive) && {
    '&&': {
      marginBlockEnd: theme.space['0']
    }
  }, $bordered !== false && {
    borderColor: theme.color['border-primary-subtle'],
    borderStyle: 'solid',
    borderWidth: theme.borderWidth['100'],
    borderTopWidth: theme.borderWidth['0']
  }, {
    '& th, & td': {
      borderColor: theme.color['border-primary-subtle'],
      borderStyle: 'solid',
      borderWidth: theme.borderWidth['0'],
      borderTopWidth: theme.borderWidth['100'],
      lineHeight: 'normal',
      verticalAlign: 'middle'
    },
    '& thead th': Object.assign({}, condensedPadding, {
      fontWeight: theme.typography['heading-200'].fontWeight,
      fontSize: theme.typography['heading-25'].fontSize,
      backgroundColor: theme.color['fill-surface-raised'],
      color: theme.color['text-core-default'],
      blockSize: tableHeaderHeight,
      textAlign: 'left',
      textTransform: 'uppercase',
      verticalAlign: tableCellVerticalAlignment,
      paddingBlockEnd: theme.space['100']
    }),
    '& tfoot td, & tfoot th': {
      fontWeight: theme.typography['heading-50'].fontWeight,
      blockSize: tableCellHeight,
      paddingBlock: theme.space['400'],
      paddingInline: theme.space['600'],
      textAlign: 'left'
    },
    '& tbody td': Object.assign({
      hyphens: 'auto',
      overflowWrap: 'break-word',
      // Reminder: prevent overflow in Chrome 55+ (issue UIComponents#1494)
      verticalAlign: tableCellVerticalAlignment,
      blockSize: $condensed ? tableCellHeightCompact : tableCellHeight
    }, $condensed ? condensedPadding : defaultPadding)
  }, $hover && {
    tbody: {
      tr: {
        transition: 'background-color 150ms ease-out'
      },
      'tr:hover': {
        backgroundColor: theme.color['fill-tertiary-hover']
      }
    }
  }, $truncated && {
    tableLayout: 'fixed',
    '& td, & th': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }, $inPanel && $bordered !== true && {
    borderColor: theme.color['border-primary-subtle'],
    borderStyle: 'solid',
    borderWidth: theme.borderWidth['0'],
    borderBottomWidth: theme.borderWidth['100']
  }, $inCard && $bordered !== true && {
    border: 'none',
    borderRadius: 'inherit',
    'thead th': {
      backgroundColor: theme.color['fill-transparent-default'],
      borderTop: 'none'
    }
  }), deepMerge($styleOverrides, $cssStyles));
});
export const Table = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      bordered = 'auto',
      children,
      condensed = false,
      flush = false,
      height,
      hover = true,
      responsive = true,
      ScrollContainer = ScrollContainerComponent,
      truncated = false,
      responsiveWrapperTokenProps = {}
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    domProps,
    tokenProps,
    callbackProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    tableVariantStyleOverrides
  } = useTableContext();
  const {
    inPanel
  } = usePanelContext();
  const {
    inCard
  } = useCardContext();
  const renderedTable = /*#__PURE__*/_jsx(StyledTable, Object.assign({}, domProps, callbackProps, restProps, {
    ref: ref,
    $bordered: bordered,
    $condensed: condensed,
    $flush: flush,
    $hover: hover,
    $responsive: responsive,
    $truncated: truncated,
    $cssStyles: cssStyles,
    $styleOverrides: tableVariantStyleOverrides !== null && tableVariantStyleOverrides !== void 0 ? tableVariantStyleOverrides : {},
    $inPanel: inPanel,
    $inCard: inCard,
    children: children
  }));
  return responsive ? /*#__PURE__*/_jsx(Section, Object.assign({
    flush: flush,
    height: height
  }, responsiveWrapperTokenProps, {
    children: /*#__PURE__*/_jsx(ScrollContainer, {
      scrollDirection: height != null ? 'both' : 'horizontal',
      maxHeight: height != null ? height : undefined,
      children: renderedTable
    })
  })) : renderedTable;
});
Table.displayName = 'Table';